import React, { useState } from 'react';
import { DownloadIcon } from 'lucide-react';
import { downloadCsv } from '../../data/csv';
import { TREND_METRICS, type ReportMonth, type TrendMetricKey } from './reportingTypes';
type TrendChartProps = {
  data: ReportMonth[];
  selectedMetrics: TrendMetricKey[];
  onToggleMetric: (metric: TrendMetricKey) => void;
};
export function TrendChart({
  data,
  selectedMetrics,
  onToggleMetric
}: TrendChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const activeMetrics = TREND_METRICS.filter(metric => selectedMetrics.includes(metric.key));
  const maxValue = Math.max(1, ...data.flatMap(month => activeMetrics.map(metric => month.metrics[metric.key])));
  const chartWidth = 1080;
  const chartHeight = 460;
  const margin = {
    top: 34,
    right: 40,
    bottom: 58,
    left: 86
  };
  const plotWidth = chartWidth - margin.left - margin.right;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const pointX = (index: number) => margin.left + (data.length <= 1 ? plotWidth / 2 : index / (data.length - 1) * plotWidth);
  const pointY = (value: number) => margin.top + plotHeight - value / maxValue * plotHeight;
  const updateHoveredMonth = (clientX: number, element: SVGSVGElement) => {
    const bounds = element.getBoundingClientRect();
    const x = (clientX - bounds.left) / bounds.width * chartWidth;
    const ratio = (x - margin.left) / plotWidth;
    const nextIndex = Math.round(ratio * (data.length - 1));
    setHoveredIndex(Math.max(0, Math.min(data.length - 1, nextIndex)));
  };
  const hoveredMonth = hoveredIndex === null ? null : data[hoveredIndex];
  const tooltipX = hoveredIndex === null ? 0 : Math.min(pointX(hoveredIndex) + 18, chartWidth - 242);
  const exportTrendData = () => {
    downloadCsv<ReportMonth>('monthly-performance-export.csv', [{
      label: 'Month',
      value: month => month.label
    }, ...activeMetrics.map(metric => ({
      label: metric.label,
      value: (month: ReportMonth) => month.metrics[metric.key]
    }))], data);
  };
  return <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6" aria-labelledby="monthly-trends-heading" data-id="element-210" data-mp="38h58dhev5gri">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-211" data-mp="3mhy3a392udv5">
        <div data-id="element-212" data-mp="3hg0d0p29eisp">
          <h2 id="monthly-trends-heading" className="text-lg font-semibold text-slate-900" data-id="element-213" data-mp="3hg8s8nluu072">
            Monthly performance
          </h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-214" data-mp="3reyawilkrood">
            Compare project pipeline, invoicing and admin-maintained targets by
            month.
          </p>
        </div>
        <button type="button" onClick={exportTrendData} disabled={activeMetrics.length === 0} className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-400" title="Export selected trend measures as CSV" data-id="element-215" data-mp="4zwrazrymkxu"><DownloadIcon className="h-4 w-4" data-id="element-216" data-mp="12kyavienvsel" />Export CSV</button>
      </div>

      <div className="mt-7 overflow-x-auto rounded-xl border border-slate-100 bg-white p-2 pb-1 md:p-4 md:pb-2" data-id="element-217" data-mp="nnfebtvqsawl">
        <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="min-w-[860px] w-full cursor-crosshair" role="img" aria-label={`Line chart showing ${activeMetrics.length ? activeMetrics.map(metric => metric.label).join(', ') : 'no selected metrics'} by month`} onMouseMove={event => updateHoveredMonth(event.clientX, event.currentTarget)} onMouseLeave={() => setHoveredIndex(null)} data-id="element-218" data-mp="slyyboqrzcmb">
          <rect x={margin.left} y={margin.top} width={plotWidth} height={plotHeight} fill="transparent" data-id="element-219" data-mp="3s3q95aqfh79r" />
          {[0, 0.25, 0.5, 0.75, 1].map(ratio => {
          const y = margin.top + plotHeight - ratio * plotHeight;
          return <g key={ratio} data-id="element-220" data-mp="3k2hfm8uiyzbg">
                <line x1={margin.left} x2={chartWidth - margin.right} y1={y} y2={y} stroke="#E2E8F0" strokeWidth="1" data-id="element-221" data-mp="3fb98xkkkxs1z" />
                <text x={margin.left - 12} y={y + 4} textAnchor="end" className="fill-slate-400 text-[11px]" data-id="element-222" data-mp="36bm4epwk064p">
                  {formatCompactCurrency(maxValue * ratio)}
                </text>
              </g>;
        })}

          {data.map((month, index) => <text key={month.key} x={pointX(index)} y={chartHeight - 16} textAnchor="middle" className="fill-slate-500 text-[11px] font-medium" data-id="element-223" data-mp="ukryvxu6yajj" data-mpi={index}>
              {month.label}
            </text>)}

          {hoveredIndex !== null && <line x1={pointX(hoveredIndex)} x2={pointX(hoveredIndex)} y1={margin.top} y2={margin.top + plotHeight} stroke="#94A3B8" strokeWidth="1" strokeDasharray="4 5" aria-hidden="true" data-id="element-224" data-mp="123hp671b9esz" />}

          {activeMetrics.map(metric => {
          const path = data.map((month, index) => {
            const command = index === 0 ? 'M' : 'L';
            return `${command} ${pointX(index)} ${pointY(month.metrics[metric.key])}`;
          }).join(' ');
          const isQuotedSeries = metric.key === 'quoted' || metric.key === 'quotedWeighted';
          return <g key={metric.key} data-id="element-225" data-mp="24j1gfliqh3vg">
                <path d={path} fill="none" stroke={metric.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={isQuotedSeries ? '7 6' : undefined} data-id="element-226" data-mp="24i8fj16gh8l8" />
                {data.map((month, index) => <circle key={month.key} cx={pointX(index)} cy={pointY(month.metrics[metric.key])} r={hoveredIndex === index ? 7 : 4.5} fill="white" stroke={metric.color} strokeWidth={hoveredIndex === index ? 3.5 : 2.5} tabIndex={0} onMouseEnter={() => setHoveredIndex(index)} onFocus={() => setHoveredIndex(index)} onBlur={() => setHoveredIndex(null)} className="transition-all duration-150" data-id="element-227" data-mp="1ju8mz6jn6qj7" data-mpi={index}>
                    <title data-id="element-228" data-mp="8eai7evhyqns">{`${metric.label}, ${month.label}: ${formatCurrency(month.metrics[metric.key])}`}</title>
                  </circle>)}
              </g>;
        })}

          {hoveredMonth && <g aria-hidden="true" data-id="element-229" data-mp="2a3zmqxpid8qg">
              <rect x={tooltipX} y={margin.top + 12} width="224" height={42 + activeMetrics.length * 23} rx="10" fill="white" stroke="#CBD5E1" strokeWidth="1" data-id="element-230" data-mp="2a4r8zhx7tu42" />
              <text x={tooltipX + 14} y={margin.top + 34} className="fill-slate-900 text-[13px] font-semibold" data-id="element-231" data-mp="37qovdqepujla">
                {hoveredMonth.label}
              </text>
              {activeMetrics.map((metric, index) => <g key={metric.key} data-id="element-232" data-mp="1ick2habfby3e" data-mpi={index}>
                  <circle cx={tooltipX + 18} cy={margin.top + 54 + index * 23} r="4" fill={metric.color} data-id="element-233" data-mp="1ibiek59b7h49" />
                  <text x={tooltipX + 30} y={margin.top + 58 + index * 23} className="fill-slate-600 text-[12px]" data-id="element-234" data-mp="2e39p5phmn42s">
                    {metric.label}
                  </text>
                  <text x={tooltipX + 210} y={margin.top + 58 + index * 23} textAnchor="end" className="fill-slate-900 text-[12px] font-semibold" data-id="element-235" data-mp="202yvyfsuhdeg">
                    {formatCurrency(hoveredMonth.metrics[metric.key])}
                  </text>
                </g>)}
            </g>}
        </svg>
      </div>

      <div className="mt-4 flex flex-wrap justify-center gap-2 border-t border-slate-100 pt-4" aria-label="Trend measures" data-id="element-236" data-mp="2j0jmdrb9ck16">
        {TREND_METRICS.map(metric => {
        const isSelected = selectedMetrics.includes(metric.key);
        return <button key={metric.key} type="button" aria-pressed={isSelected} onClick={() => onToggleMetric(metric.key)} className={`inline-flex min-h-8 items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 ${isSelected ? 'border-slate-300 bg-slate-50 text-slate-800' : 'border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:text-slate-600'}`} data-id="element-237" data-mp="3j1ocazn6qpow">
              <span className="h-2.5 w-2.5 rounded-full" style={{
            backgroundColor: metric.color
          }} aria-hidden="true" data-id="element-238" data-mp="arv5psoes2xi" />
              {metric.label}
            </button>;
      })}
      </div>

      {activeMetrics.length === 0 && <p className="-mt-2 text-sm text-slate-500" role="status" data-id="element-239" data-mp="tjxkeyhhp954">
          Select at least one metric to compare monthly performance.
        </p>}
    </section>;
}
function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(value);
}
function formatCompactCurrency(value: number) {
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}m`;
  if (value >= 1000) return `£${Math.round(value / 1000)}k`;
  return `£${Math.round(value)}`;
}